/**
 * Small, allocation-light one-shot builders shared by race effects and the
 * interface. Every voice disconnects itself when it ends so long sessions do
 * not accumulate dead nodes on the graph.
 */
export interface NoiseShot {
  at?: number;
  duration: number;
  gain: number;
  attack?: number;
  /** Exponential decay time constant; defaults to a duration-fitted curve. */
  filter?: { type: BiquadFilterType; from: number; to?: number; q?: number };
  pan?: number;
  playbackRate?: number;
  send?: AudioNode | null;
  sendGain?: number;
}

export interface ToneShot {
  at?: number;
  type?: OscillatorType;
  from: number;
  to?: number;
  duration: number;
  gain: number;
  attack?: number;
  pan?: number;
  detune?: number;
  filter?: { type: BiquadFilterType; frequency: number; q?: number };
  send?: AudioNode | null;
  sendGain?: number;
}

export function playNoise(
  context: BaseAudioContext,
  noise: AudioBuffer,
  destination: AudioNode,
  shot: NoiseShot,
): void {
  const start = shot.at ?? context.currentTime;
  const source = context.createBufferSource();
  source.buffer = noise;
  source.loop = true;
  source.loopStart = 0;
  source.playbackRate.value = shot.playbackRate ?? 1;
  const envelope = context.createGain();
  shape(envelope.gain, start, shot.attack ?? 0.002, shot.duration, shot.gain);
  let node: AudioNode = source;
  if (shot.filter) {
    const filter = context.createBiquadFilter();
    filter.type = shot.filter.type;
    filter.Q.value = shot.filter.q ?? 0.8;
    filter.frequency.setValueAtTime(shot.filter.from, start);
    if (shot.filter.to) filter.frequency.exponentialRampToValueAtTime(Math.max(20, shot.filter.to), start + shot.duration);
    node = node.connect(filter);
  }
  node.connect(envelope);
  const output = route(context, envelope, destination, shot.pan, shot.send, shot.sendGain);
  // Random offset into the loop keeps repeated hits from sounding identical.
  source.start(start, Math.random() * Math.max(0, noise.duration - 0.5));
  source.stop(start + shot.duration + 0.05);
  source.onended = () => output.disconnect();
}

export function playTone(context: BaseAudioContext, destination: AudioNode, shot: ToneShot): void {
  const start = shot.at ?? context.currentTime;
  const oscillator = context.createOscillator();
  oscillator.type = shot.type ?? 'sine';
  oscillator.frequency.setValueAtTime(shot.from, start);
  if (shot.to) oscillator.frequency.exponentialRampToValueAtTime(Math.max(10, shot.to), start + shot.duration);
  if (shot.detune) oscillator.detune.value = shot.detune;
  const envelope = context.createGain();
  shape(envelope.gain, start, shot.attack ?? 0.003, shot.duration, shot.gain);
  let node: AudioNode = oscillator;
  if (shot.filter) {
    const filter = context.createBiquadFilter();
    filter.type = shot.filter.type;
    filter.frequency.value = shot.filter.frequency;
    filter.Q.value = shot.filter.q ?? 0.7;
    node = node.connect(filter);
  }
  node.connect(envelope);
  const output = route(context, envelope, destination, shot.pan, shot.send, shot.sendGain);
  oscillator.start(start);
  oscillator.stop(start + shot.duration + 0.05);
  oscillator.onended = () => output.disconnect();
}

/** Two-operator FM bell: glassy chimes for rewards and interface confirms. */
export function playBell(
  context: BaseAudioContext,
  destination: AudioNode,
  frequency: number,
  options: { at?: number; duration?: number; gain: number; pan?: number; ratio?: number; index?: number; send?: AudioNode | null; sendGain?: number },
): void {
  const start = options.at ?? context.currentTime;
  const duration = options.duration ?? 0.6;
  const carrier = context.createOscillator();
  carrier.frequency.value = frequency;
  const modulator = context.createOscillator();
  modulator.frequency.value = frequency * (options.ratio ?? 3.5);
  const modulation = context.createGain();
  const index = (options.index ?? 2.2) * frequency;
  modulation.gain.setValueAtTime(index, start);
  modulation.gain.exponentialRampToValueAtTime(Math.max(1, index * 0.04), start + duration * 0.6);
  modulator.connect(modulation).connect(carrier.frequency);
  const envelope = context.createGain();
  shape(envelope.gain, start, 0.002, duration, options.gain);
  carrier.connect(envelope);
  const output = route(context, envelope, destination, options.pan, options.send, options.sendGain);
  carrier.start(start);
  modulator.start(start);
  carrier.stop(start + duration + 0.05);
  modulator.stop(start + duration + 0.05);
  carrier.onended = () => output.disconnect();
}

/** Struck sheet metal: a burst through a few inharmonic resonators. */
export function playMetal(
  context: BaseAudioContext,
  noise: AudioBuffer,
  destination: AudioNode,
  options: { at?: number; gain: number; pan?: number; pitch?: number; decay?: number; send?: AudioNode | null },
): void {
  const start = options.at ?? context.currentTime;
  const pitch = options.pitch ?? 1;
  const decay = options.decay ?? 0.35;
  const source = context.createBufferSource();
  source.buffer = noise;
  source.loop = true;
  const exciter = context.createGain();
  exciter.gain.setValueAtTime(1, start);
  exciter.gain.exponentialRampToValueAtTime(0.0001, start + 0.025);
  source.connect(exciter);
  const sum = context.createGain();
  sum.gain.value = options.gain;
  for (const [frequency, q, level] of [[420, 28, 1], [1_130, 32, 0.8], [2_270, 36, 0.55], [3_410, 40, 0.4]] as const) {
    const resonator = context.createBiquadFilter();
    resonator.type = 'bandpass';
    resonator.frequency.value = frequency * pitch * (0.94 + Math.random() * 0.12);
    resonator.Q.value = q * (decay / 0.35);
    const level_ = context.createGain();
    level_.gain.value = level * 6;
    exciter.connect(resonator).connect(level_).connect(sum);
  }
  const output = route(context, sum, destination, options.pan, options.send, 0.5);
  source.start(start, Math.random() * Math.max(0, noise.duration - 0.5));
  source.stop(start + decay * 2.5);
  source.onended = () => output.disconnect();
}

function shape(param: AudioParam, start: number, attack: number, duration: number, gain: number): void {
  const peak = Math.max(0.00011, gain);
  param.setValueAtTime(0.0001, start);
  param.linearRampToValueAtTime(peak, start + Math.max(0.001, attack));
  param.exponentialRampToValueAtTime(0.0001, start + Math.max(attack + 0.005, duration));
}

function route(
  context: BaseAudioContext,
  node: AudioNode,
  destination: AudioNode,
  pan: number | undefined,
  send: AudioNode | null | undefined,
  sendGain = 0.3,
): AudioNode {
  let output = node;
  if (pan !== undefined && pan !== 0 && typeof context.createStereoPanner === 'function') {
    const panner = context.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    output = node.connect(panner);
  }
  output.connect(destination);
  if (send) {
    const sendLevel = context.createGain();
    sendLevel.gain.value = sendGain;
    output.connect(sendLevel).connect(send);
  }
  return output;
}

/** Shared mono-ish noise source material (2 s, slightly pink). */
export function createNoiseBuffer(context: BaseAudioContext): AudioBuffer {
  const length = Math.floor(context.sampleRate * 2);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const channel = buffer.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let index = 0; index < length; index += 1) {
    const white = Math.random() * 2 - 1;
    // Paul Kellet's economy pink filter, blended with white for air.
    b0 = 0.99765 * b0 + white * 0.099046;
    b1 = 0.963 * b1 + white * 0.2965164;
    b2 = 0.57 * b2 + white * 1.0526913;
    channel[index] = (b0 + b1 + b2 + white * 0.1848) * 0.16 + white * 0.25;
  }
  return buffer;
}
